'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useParams, usePathname } from 'next/navigation';

export type ChatMode = 'research' | 'alfa' | 'brain' | 'strategy' | 'predic';
export type AgentMode =
  | 'research'
  | 'sla'
  | 'alfa'
  | 'gpt-5'
  | 'gemini-2.5'
  | 'grok-4'
  | 'claude'
  | 'predic';
interface ChatModeContextType {
  mode: ChatMode;
  changeMode: (mode: ChatMode) => void;
  isWorkspace: boolean;
  agent?: AgentMode;
  changeAgent: (agent: AgentMode) => void;
  workspaceId?: string; // equal to teamId, workspace is team, tema is workspace
}

const ChatModeContext = createContext<ChatModeContextType | undefined>(undefined);

export const useChatMode = () => {
  const context = useContext(ChatModeContext);
  if (context === undefined) {
    throw new Error('useChats must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatModeProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ChatMode>('alfa');
  const [agent, setAgent] = useState<AgentMode>('alfa');
  const pathname = usePathname();
  const { workspaceId } = useParams();
  const isWorkspace = pathname.startsWith('/workspace/');

  useEffect(() => {
    const url = new URL(location.href);
    const isResearch = url.searchParams.get('is_research');
    const agentMode = url.searchParams.get('agent') as AgentMode;
    if (pathname.startsWith('/predic')) {
      setMode('predic');
      setAgent('predic');
    } else if (
      pathname.startsWith('/r/') ||
      pathname.startsWith('/research') ||
      pathname.startsWith('/notion') ||
      isResearch
    ) {
      setMode('research');
      setAgent('research');
    } else if (isWorkspace) {
      setMode(agentMode === 'sla' ? 'strategy' : 'brain');
      setAgent(agentMode || 'gpt-5');
    } else {
      setMode('alfa');
      setAgent('alfa');
    }
  }, [pathname, isWorkspace]);

  const value: ChatModeContextType = {
    mode,
    agent,
    changeMode: setMode,
    changeAgent: setAgent,
    isWorkspace,
    workspaceId: isWorkspace && workspaceId ? (workspaceId as string) : undefined,
  };

  return <ChatModeContext.Provider value={value}>{children}</ChatModeContext.Provider>;
};
